import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Gift, Star, Loader2 } from 'lucide-react';
import { rewardsAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const AdminRewards = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', points_required: '', category: 'Service',
    reward_type: 'free_service', value: '', is_active: true
  });

  const categories = ['Service', 'Discount', 'Credit', 'Product'];
  const rewardTypes = [
    { value: 'free_service', label: 'Free Service' },
    { value: 'discount', label: 'Discount %' },
    { value: 'credit', label: 'Store Credit' }
  ];

  useEffect(() => { fetchRewards(); }, []);

  const fetchRewards = async () => {
    try {
      const response = await rewardsAPI.getAllAdmin();
      setRewards(response.data.rewards || []);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (reward = null) => {
    if (reward) {
      setEditing(reward);
      setForm({
        name: reward.name, description: reward.description, points_required: reward.points_required.toString(),
        category: reward.category, reward_type: reward.reward_type, value: reward.value?.toString() || '', is_active: reward.is_active
      });
    } else {
      setEditing(null);
      setForm({ name: '', description: '', points_required: '', category: 'Service', reward_type: 'free_service', value: '', is_active: true });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.points_required) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const data = { ...form, points_required: parseInt(form.points_required), value: form.value ? parseFloat(form.value) : null };
      if (editing) {
        await rewardsAPI.update(editing.id, data);
        toast.success('Reward updated');
      } else {
        await rewardsAPI.create(data);
        toast.success('Reward created');
      }
      fetchRewards();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save reward');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reward) => {
    if (!window.confirm(`Delete "${reward.name}"?`)) return;
    try {
      await rewardsAPI.delete(reward.id);
      toast.success('Reward deleted');
      fetchRewards();
    } catch (error) {
      toast.error('Failed to delete reward');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = { Service: '💇‍♀️', Discount: '🏷️', Credit: '💳', Product: '🎁' };
    return icons[category] || '🎁';
  };

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#4A154B]">Rewards</h1>
          <p className="text-gray-600">Manage loyalty rewards</p>
        </div>
        <Button onClick={() => openModal()} className="btn-primary" data-testid="add-reward-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Reward
        </Button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton h-40 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rewards.map((reward) => (
            <div key={reward.id} className={`glass rounded-2xl p-5 card-hover ${!reward.is_active ? 'opacity-60' : ''}`} data-testid={`reward-card-${reward.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getCategoryIcon(reward.category)}</span>
                  <div>
                    <h3 className="font-serif text-lg text-[#4A154B]">{reward.name}</h3>
                    <span className="text-xs text-[#C9A689] font-medium uppercase">{reward.category}</span>
                  </div>
                </div>
                {!reward.is_active && <span className="bg-gray-500 text-white px-2 py-1 rounded-full text-xs">Inactive</span>}
              </div>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{reward.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-[#F5E6D3]">
                <span className="font-semibold text-[#C9A689] flex items-center gap-1">
                  <Star className="w-4 h-4" /> {reward.points_required} pts
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openModal(reward)} data-testid={`edit-reward-${reward.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(reward)} className="text-red-500 hover:text-red-700" data-testid={`delete-reward-${reward.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">
              {editing ? 'Edit Reward' : 'Add Reward'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="form-label">Name *</label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Reward name" data-testid="reward-name-input" />
              </div>
              <div>
                <label className="form-label">Points Required *</label>
                <Input type="number" value={form.points_required} onChange={(e) => setForm({...form, points_required: e.target.value})} placeholder="100" min="1" data-testid="reward-points-input" />
              </div>
              <div>
                <label className="form-label">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="reward-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Reward Type</label>
                <Select value={form.reward_type} onValueChange={(v) => setForm({...form, reward_type: v})}>
                  <SelectTrigger data-testid="reward-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {rewardTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Value (% or $)</label>
                <Input type="number" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})} placeholder="10" min="0" data-testid="reward-value-input" />
              </div>
              <div className="col-span-2">
                <label className="form-label">Description</label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Reward description" rows={3} data-testid="reward-description-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(c) => setForm({...form, is_active: c})} data-testid="reward-active-switch" />
                <span className="text-sm">Active</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-reward-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRewards;
